/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
import { clamp } from '../Math';
import type { Matrix4 } from '../matrix/Matrix4';
import { Vector3 } from '../vector/Vector3';

export class Line3 {
  public start: Vector3;
  public end: Vector3;

  constructor(start?: Vector3, end?: Vector3) {
    this.start = start !== undefined ? start : new Vector3();
    this.end = end !== undefined ? end : new Vector3();
  }

  public set(start: Vector3, end: Vector3): this {
    this.start.copy(start);
    this.end.copy(end);

    return this;
  }

  public clone(): Line3 {
    return new Line3().copy(this);
  }

  public copy(line: Line3): this {
    this.start.copy(line.start);
    this.end.copy(line.end);

    return this;
  }

  public getCenter(out: Vector3): Vector3 {
    return out.addVectors(this.start, this.end).multiplyScalar(0.5);
  }

  public delta(out: Vector3): Vector3 {
    return out.subVectors(this.end, this.start);
  }

  public distanceSq(): number {
    return this.start.distanceToSquared(this.end);
  }

  public distance(): number {
    return this.start.distanceTo(this.end);
  }

  public at(t: number, out: Vector3): Vector3 {
    return this.delta(out).multiplyScalar(t).add(this.start);
  }

  public closestPointToPointParameter(
    point: Vector3,
    clampToLine: boolean,
  ): number {
    startP.subVectors(point, this.start);
    startEnd.subVectors(this.end, this.start);

    const startEnd2 = startEnd.dot(startEnd);
    const startEndStartP = startEnd.dot(startP);

    let t = startEndStartP / startEnd2;

    if (clampToLine) {
      t = clamp(t, 0, 1);
    }

    return t;
  }

  public closestPointToPoint(
    point: Vector3,
    clampToLine: boolean,
    out: Vector3,
  ): Vector3 {
    const t = this.closestPointToPointParameter(point, clampToLine);

    return this.delta(out).multiplyScalar(t).add(this.start);
  }

  public applyMatrix4(matrix: Matrix4): this {
    this.start.applyMatrix4(matrix);
    this.end.applyMatrix4(matrix);

    return this;
  }

  public equals(line: Line3): boolean {
    return line.start.equals(this.start) && line.end.equals(this.end);
  }
}

const startP = new Vector3();
const startEnd = new Vector3();
